import { HttpClient } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { Router } from '@angular/router';
import { AppUserContextService } from '@app/core/services/app-user-context.service';
import { DebugLogService } from '@app/core/services/debug-log.service';
import { AppNames } from '@app/models/AppNames';
import { IPrivileges } from '@app/models/user-privileges.model';
import { summarizeToken } from '@app/shared/utils/debug-utility';
import { decodeJwt, extractRoles } from '@app/shared/utils/utility';
import {
  AuthModule,
  LogLevel,
  OidcSecurityService,
  StsConfigHttpLoader,
  StsConfigLoader
} from 'angular-auth-oidc-client';
import { environment } from 'src/environments/environment';

export const RENEW_BEFORE_SECONDS = 30;

export const httpLoaderFactory = () => {
  const unityExpressConfig$ = Promise.resolve({
    configId: AppNames.UNITY_EXPRESS,
    authority: environment.appConfig.stsServer,
    redirectUrl: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    clientId: environment.appConfig.clientId,
    scope: environment.appConfig.scope,
    responseType: environment.appConfig.responseType, // implicit
    silentRenew: true,
    silentRenewUrl: window.location.origin + '/silent-renew.html',
    renewTimeBeforeTokenExpiresInSeconds: RENEW_BEFORE_SECONDS,
    secureRoutes: [
      environment.appConfig.apiRoot,
      environment.appConfig.apiRootV2,
      environment.appConfig.proposalsApiRoot,
      environment.appConfig.vinApiRoot,
      environment.appConfig.identityRoot,
      environment.appConfig.accountsApiRoot,
      environment.appConfig.stsServer
    ],
    startCheckSession: true,
    customParamsAuthRequest: {
      acr_values: 'idp:hub'
    },
    logLevel: LogLevel.None,
    autoUserInfo: false
  });
  const unityDirectConfig$ = Promise.resolve({
    configId: AppNames.UNITY_DIRECT,
    authority: environment.appConfig.stsServer,
    redirectUrl: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    clientId: environment.appConfig.clientId,
    scope: environment.appConfig.scope,
    responseType: environment.appConfig.responseType, // implicit
    useRefreshToken: true,
    silentRenew: true,
    ignoreNonceAfterRefresh: true,
    triggerRefreshWhenIdTokenExpired: false,
    silentRenewUrl: window.location.origin + '/silent-renew.html',
    renewTimeBeforeTokenExpiresInSeconds: RENEW_BEFORE_SECONDS,
    renewUserInfoAfterTokenRenew: true,
    secureRoutes: [
      environment.appConfig.headerURL,
      environment.appConfig.apiRoot,
      environment.appConfig.proposalsApiRoot,
      environment.appConfig.vinApiRoot,
      environment.appConfig.identityRoot,
      environment.appConfig.accountsApiRoot,
      environment.appConfig.stsServer
    ],
    startCheckSession: true,
    logLevel: LogLevel.None,
    autoUserInfo: false
  });
  if (sessionStorage.getItem('appName') === AppNames.UNITY_EXPRESS) {
    return new StsConfigHttpLoader(unityExpressConfig$);
  } else if (sessionStorage.getItem('appName') === AppNames.UNITY_DIRECT) {
    return new StsConfigHttpLoader(unityDirectConfig$);
  }
};
@NgModule({
  imports: [
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  exports: [AuthModule],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: init,
      deps: [
        OidcSecurityService,
        Router,
        AppUserContextService,
        DebugLogService
      ],
      multi: true
    }
  ]
})
export class AuthConfigModule { }

/**
 * Entry point for authorization. Returns a Promise
 * @param oidcSecurityService {OidcSecurityService}
 * @param router {Router}
 * @param appUserContextService {AppUserContextService}
 * @returns {() => Promise<unknown>}
 */
function init(
  oidcSecurityService: OidcSecurityService,
  router: Router,
  appUserContextService: AppUserContextService,
  debugLogService: DebugLogService
): () => Promise<unknown> {
  return () =>
    new Promise<unknown>((resolve: any) => {
      processByApp(
        resolve,
        oidcSecurityService,
        router,
        appUserContextService,
        debugLogService
      );
    });
}

/**
 * Resolve based on app (UD/UE)
 * @param resolve {any}
 * @param oidcSecurityService {OidcSecurityService}
 * @param router {Router}
 * @param appUserContextService {AppUserContextService}
 */
function processByApp(
  resolve: any,
  oidcSecurityService: OidcSecurityService,
  router: Router,
  appUserContextService: AppUserContextService,
  debugLogService: DebugLogService
): void {
  const appName = sessionStorage.getItem('appName');
  debugLogService.info('AUTH - processByApp', { appName });

  if (appName !== AppNames.UNITY_DIRECT && appName !== AppNames.UNITY_EXPRESS) {
    debugLogService.warn('Unexpected appName - bypassing auth', { appName });
    debugLogService.flush({ appName, step: 'processByApp' });
    return;
  }

  if (appName === AppNames.UNITY_EXPRESS) {
    debugLogService.info('AUTH - EXPRESS: bypass auth initializer');
    debugLogService.flush({ appName, mode: 'express-bypass' });
    resolve(true);
  } else if (appName === AppNames.UNITY_DIRECT) {
    debugLogService.info('AUTH - DIRECT: Checking AUTHORIZATION');
    debugLogService.flush({ appName, mode: 'DIRECT' });
    setTimeout(() => {
      oidcSecurityService.checkAuth().subscribe({
        next: (res: any) => {
          debugLogService.info('AUTH - DIRECT: Initiating Auth');
          if (!res.isAuthenticated) {
            debugLogService.info(
              'AUTH - DIRECT: User not Authenticated. Will call authorize()'
            );
            oidcSecurityService.authorize();
          }
          if (res.accessToken) {
            debugLogService.info('AUTH - DIRECT: SUCCESS TOKEN AVAILABLE');
            logTokenData(appName, res.accessToken, debugLogService);

            const claims = decodeJwt(res.accessToken);
            const userRoles = extractRoles(claims);
            appUserContextService.userRoles = [...userRoles];

            appUserContextService.setToken(res.accessToken);
            localStorage.setItem('access_token', res.accessToken);

            const proposalId = sessionStorage.getItem('proposalId');
            if (proposalId) {
              const reviewUrl =
                '/review?id=' + sessionStorage.getItem('proposalId');

              debugLogService.info('AUTH - DIRECT: proposalId redirect', {
                reviewUrl
              });
              debugLogService.flush({ appName, step: 'Nav to Review Page' });

              router.navigateByUrl(reviewUrl);
              sessionStorage.setItem('proposalId', '');
            }

            debugLogService.info('AUTH - DIRECT: Getting privileges');

            appUserContextService.getPrivileges().subscribe({
              next: (response: IPrivileges) => {
                appUserContextService.setPrivileges(response);

                debugLogService.info(
                  'AUTH - DIRECT: GET PRIVILEGES SUCCESS',
                  response
                );
                debugLogService.info('AUTH - DIRECT:  INIT RESOLVE');
                debugLogService.flush({ appName, mode: 'Privileges SUCCESS' });

                resolve(true);
              },
              error: (err: any) => {
                debugLogService.error(
                  'AUTH - DIRECT:  Failed to get Privileges',
                  err
                );
                debugLogService.flush({ appName, mode: 'Privileges ERROR' });
                resolve(true);
              }
            });
          }
        },
        error: (err: any) => {
          debugLogService.error('AUTH - DIRECT: checkAuth ERROR', err);
          debugLogService.flush({ appName, step: 'checkAuth' });
          resolve(true);
        }
      });
    }, 2000);
  }
}

function logTokenData(
  appName: string,
  token: string,
  debugLogService: DebugLogService
): void {
  try {
    const tokenSummary = summarizeToken(token, RENEW_BEFORE_SECONDS);
    debugLogService.info('AUTH - DIRECT: TOKEN SUMMARY ', {
      user: tokenSummary.user,
      iatIso: tokenSummary.iatIso,
      expIso: tokenSummary.expIso,
      secondsRemaining: tokenSummary.secondsRemaining,
      renewAtIso: tokenSummary.renewAtIso,
      iss: tokenSummary.iss,
      aud: tokenSummary.aud,
      azp: tokenSummary.azp
    });
    debugLogService.info('AUTH - DIRECT: TOKEN ACCESS', {
      scopes: tokenSummary.scopes,
      roles: tokenSummary.roles
    });
    debugLogService.flush({ appName, mode: 'Token Summarized' });
  } catch {
    debugLogService.error(
      'AUTH - DIRECT: Unable to get token summary. Check token manually'
    );
    debugLogService.flush({ appName, step: 'Token Summary' });
  }
}
